import React, { useMemo, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, UsersIcon, NetworkIcon, UserIcon, ChevronRightIcon, DollarSignIcon } from 'lucide-react';
import { ReferralStats } from '../utils/referralCalculations';
import { ReferralNetwork } from '../data/mockReferralData';
import { ReferralTreeView } from './ReferralTreeView';
type SortField = 'name' | 'direct' | 'indirect' | 'total' | 'directFundings' | 'indirectFundings';
type SortDirection = 'asc' | 'desc';
type ReferralTableProps = {
  stats: ReferralStats[];
  network: ReferralNetwork;
};
// Format funding amount (in thousands) to readable string
function formatFunding(amount: number): string {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2)}M`;
  }
  return `$${amount}K`;
}
export function ReferralTable({
  stats,
  network
}: ReferralTableProps) {
  const [sortField, setSortField] = useState<SortField>('total');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const sortedStats = useMemo(() => {
    return [...stats].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.employee.name.localeCompare(b.employee.name);
          break;
        case 'direct':
          comparison = a.directCount - b.directCount;
          break;
        case 'indirect':
          comparison = a.indirectCount - b.indirectCount;
          break;
        case 'total':
          comparison = a.totalNetworkImpact - b.totalNetworkImpact;
          break;
        case 'directFundings':
          comparison = a.directFundings - b.directFundings;
          break;
        case 'indirectFundings':
          comparison = a.indirectFundings - b.indirectFundings;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [stats, sortField, sortDirection]);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const handleToggleExpand = (employeeId: string) => {
    setExpandedEmployeeId(expandedEmployeeId === employeeId ? null : employeeId);
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="w-4 h-4 text-gray-300" data-id="element-44" />;
    }
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 text-blue-600" data-id="element-45" /> : <ChevronDownIcon className="w-4 h-4 text-blue-600" data-id="element-46" />;
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-47">
      <div className="overflow-x-auto" data-id="element-48">
        <table className="w-full" data-id="element-49">
          <thead data-id="element-50">
            <tr className="bg-gray-50 border-b border-gray-200" data-id="element-51">
              <th className="w-12" data-id="element-52"></th>
              <th className="text-left px-6 py-4" data-id="element-53">
                <button onClick={() => handleSort('name')} className="flex items-center gap-2 font-semibold text-gray-700 hover:text-blue-600 transition-colors" aria-label="Sort by employee name" data-id="element-54">
                  <UserIcon className="w-4 h-4" data-id="element-55" />
                  Referrer (Employee)
                  <SortIcon field="name" data-id="element-56" />
                </button>
              </th>
              <th className="text-center px-6 py-4" data-id="element-57">
                <button onClick={() => handleSort('direct')} className="flex items-center justify-center gap-2 font-semibold text-gray-700 hover:text-blue-600 transition-colors mx-auto" aria-label="Sort by direct referrals" data-id="element-58">
                  <UsersIcon className="w-4 h-4" data-id="element-59" />
                  Direct Referrals
                  <SortIcon field="direct" data-id="element-60" />
                </button>
              </th>
              <th className="text-center px-6 py-4" data-id="element-61">
                <button onClick={() => handleSort('indirect')} className="flex items-center justify-center gap-2 font-semibold text-gray-700 hover:text-blue-600 transition-colors mx-auto" aria-label="Sort by indirect referrals" data-id="element-62">
                  <NetworkIcon className="w-4 h-4" data-id="element-63" />
                  Indirect Referrals
                  <SortIcon field="indirect" data-id="element-64" />
                </button>
              </th>
              <th className="text-center px-6 py-4" data-id="element-65">
                <button onClick={() => handleSort('directFundings')} className="flex items-center justify-center gap-2 font-semibold text-gray-700 hover:text-blue-600 transition-colors mx-auto" aria-label="Sort by direct fundings" data-id="element-66">
                  <DollarSignIcon className="w-4 h-4" data-id="element-67" />
                  Direct Fundings
                  <SortIcon field="directFundings" data-id="element-68" />
                </button>
              </th>
              <th className="text-center px-6 py-4" data-id="element-69">
                <button onClick={() => handleSort('indirectFundings')} className="flex items-center justify-center gap-2 font-semibold text-gray-700 hover:text-blue-600 transition-colors mx-auto" aria-label="Sort by indirect fundings" data-id="element-70">
                  <DollarSignIcon className="w-4 h-4" data-id="element-71" />
                  Indirect Fundings
                  <SortIcon field="indirectFundings" data-id="element-72" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody data-id="element-73">
            {sortedStats.map((stat, index) => {
            const isExpanded = expandedEmployeeId === stat.employee.id;
            const hasReferrals = stat.totalNetworkImpact > 0;
            return <>
                  <tr key={stat.employee.id} onClick={() => hasReferrals && handleToggleExpand(stat.employee.id)} className={`
                      transition-colors
                      ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                      ${hasReferrals ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default'}
                      ${isExpanded ? 'bg-blue-50' : ''}
                    `} tabIndex={hasReferrals ? 0 : undefined} role={hasReferrals ? 'button' : undefined} aria-label={hasReferrals ? `Toggle referral tree for ${stat.employee.name}` : undefined} aria-expanded={hasReferrals ? isExpanded : undefined} onKeyDown={e => {
                if (hasReferrals && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleToggleExpand(stat.employee.id);
                }
              }} data-id="element-74">
                    <td className="px-4 py-4" data-id="element-75">
                      {hasReferrals && <ChevronRightIcon className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} data-id="element-76" />}
                    </td>
                    <td className="px-6 py-4" data-id="element-77">
                      <div className="flex flex-col" data-id="element-78">
                        <span className="font-medium text-gray-900" data-id="element-79">
                          {stat.employee.name}
                        </span>
                        <span className="text-sm text-gray-500" data-id="element-80">
                          {stat.employee.title} • {stat.employee.department}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-81">
                      <span className="inline-flex items-center justify-center min-w-[2.5rem] px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold text-lg" data-id="element-82">
                        {stat.directCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-83">
                      <span className={`
                        inline-flex items-center justify-center min-w-[2.5rem] px-3 py-1 rounded-full font-semibold text-lg
                        ${stat.indirectCount > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}
                      `} data-id="element-84">
                        {stat.indirectCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-85">
                      <span className={`
                        inline-flex items-center justify-center min-w-[4rem] px-3 py-1 rounded-full font-semibold text-base
                        ${stat.directFundings > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}
                      `} data-id="element-86">
                        {stat.directFundings > 0 ? formatFunding(stat.directFundings) : '$0'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-87">
                      <span className={`
                        inline-flex items-center justify-center min-w-[4rem] px-3 py-1 rounded-full font-semibold text-base
                        ${stat.indirectFundings > 0 ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}
                      `} data-id="element-88">
                        {stat.indirectFundings > 0 ? formatFunding(stat.indirectFundings) : '$0'}
                      </span>
                    </td>
                  </tr>

                  {/* Inline Tree View */}
                  {isExpanded && <tr data-id="element-89">
                      <td colSpan={7} className="p-0" data-id="element-90">
                        <div className="bg-gray-50 border-t border-b border-gray-200" data-id="element-91">
                          <div className="p-6" data-id="element-92">
                            <ReferralTreeView employeeId={stat.employee.id} network={network} inline={true} data-id="element-93" />
                          </div>
                        </div>
                      </td>
                    </tr>}
                </>;
          })}
          </tbody>
        </table>
      </div>

      {/* Table footer with legend */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-94">
        <div className="flex flex-wrap gap-6 text-sm text-gray-600" data-id="element-95">
          <div className="flex items-center gap-2" data-id="element-96">
            <span className="w-3 h-3 rounded-full bg-blue-100 border border-blue-300" data-id="element-97"></span>
            <span data-id="element-98">Direct: First-level referrals</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-99">
            <span className="w-3 h-3 rounded-full bg-purple-100 border border-purple-300" data-id="element-100"></span>
            <span data-id="element-101">Indirect: Downstream referrals (2nd level+)</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-102">
            <span className="w-3 h-3 rounded-full bg-emerald-100 border border-emerald-300" data-id="element-103"></span>
            <span data-id="element-104">Direct Fundings: Loan volume from direct referrals</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-105">
            <span className="w-3 h-3 rounded-full bg-teal-100 border border-teal-300" data-id="element-106"></span>
            <span data-id="element-107">Indirect Fundings: Loan volume from indirect referrals</span>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500" data-id="element-108">
          Click on any row with referrals to expand the referral tree
        </p>
      </div>
    </div>;
}